import React, { useState, useEffect } from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Search, Eye } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { adminAPI } from '../../lib/api';
import { formatPrice, calculateSavingsPercent } from '../../lib/utils';
import { toast } from 'sonner';

export default function LooksPage() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [newLook, setNewLook] = useState({
    name: '',
    slug: '',
    style: '',
    occasion: '',
    image: '',
    bundle_price_cents: '',
    is_active: true
  });
  const [editLook, setEditLook] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    loadLooks();
  }, [page]);

  const loadLooks = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getLooks({ page, limit: 20 });
      setLooks(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error loading looks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce look ?')) return;
    try {
      await adminAPI.deleteLook(id);
      toast.success('Look supprimé');
      loadLooks();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddLook = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createLook({
        name: newLook.name,
        slug: newLook.slug,
        style: newLook.style,
        occasion: newLook.occasion,
        images: newLook.image ? [newLook.image] : [],
        items: [],
        bundle_price_cents: newLook.bundle_price_cents ? Number(newLook.bundle_price_cents) : undefined,
        is_active: newLook.is_active
      });
      toast.success('Look ajouté');
      setShowAdd(false);
      setNewLook({ name: '', slug: '', style: '', occasion: '', image: '', bundle_price_cents: '', is_active: true });
      loadLooks();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleEditLook = (look) => {
    setEditLook({
      ...look,
      image: look.images?.[0] || '',
      bundle_price_cents: look.bundle_price_cents || '',
    });
    setShowEdit(true);
  };

  const handleUpdateLook = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateLook(editLook.id, {
        name: editLook.name,
        slug: editLook.slug,
        style: editLook.style,
        occasion: editLook.occasion,
        images: editLook.image ? [editLook.image] : [],
        items: editLook.items || [],
        bundle_price_cents: editLook.bundle_price_cents ? Number(editLook.bundle_price_cents) : undefined,
        is_active: editLook.is_active
      });
      toast.success('Look modifié');
      setShowEdit(false);
      setEditLook(null);
      loadLooks();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-looks">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Looks</h1>
          <p className="text-muted-foreground">Gérez vos looks et bundles</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau look
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleAddLook} className="space-y-4">
              <DialogHeader>
                <DialogTitle>Ajouter un look</DialogTitle>
              </DialogHeader>
              <Input required placeholder="Nom" value={newLook.name} onChange={e => setNewLook(p => ({ ...p, name: e.target.value }))} />
              <Input required placeholder="Slug (unique)" value={newLook.slug} onChange={e => setNewLook(p => ({ ...p, slug: e.target.value }))} />
              <Input required placeholder="Image (URL)" value={newLook.image} onChange={e => setNewLook(p => ({ ...p, image: e.target.value }))} />
              <Input placeholder="Style" value={newLook.style} onChange={e => setNewLook(p => ({ ...p, style: e.target.value }))} />
              <Input placeholder="Occasion" value={newLook.occasion} onChange={e => setNewLook(p => ({ ...p, occasion: e.target.value }))} />
              <Input type="number" placeholder="Prix bundle (centimes)" value={newLook.bundle_price_cents} onChange={e => setNewLook(p => ({ ...p, bundle_price_cents: e.target.value }))} />
              <div className="flex gap-2 items-center">
                <label className="text-sm">Actif</label>
                <input type="checkbox" checked={newLook.is_active} onChange={e => setNewLook(p => ({ ...p, is_active: e.target.checked }))} />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="secondary">Annuler</Button>
                </DialogClose>
                <Button type="submit">Ajouter</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher un look..." className="pl-10" />
          </div>
        </CardContent>
      </Card>

      {/* Looks Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Look</TableHead>
                <TableHead>Style</TableHead>
                <TableHead>Articles</TableHead>
                <TableHead>Prix Bundle</TableHead>
                <TableHead>Économie</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-32" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-20" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-12" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-12" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16" /></TableCell>
                    <TableCell><div className="h-4 bg-secondary animate-pulse rounded w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : looks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucun look trouvé
                  </TableCell>
                </TableRow>
              ) : (
                looks.map((look, index) => {
                  const savingsPercent = calculateSavingsPercent(
                    look.original_price_cents,
                    look.bundle_price_cents
                  );
                  return (
                    <motion.tr
                      key={look.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="table-row-hover"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-16 bg-secondary rounded overflow-hidden flex-shrink-0">
                            {look.images?.[0] && (
                              <img
                                src={look.images[0]}
                                alt={look.name}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{look.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{look.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {look.style && <Badge variant="outline">{look.style}</Badge>}
                          {look.occasion && <Badge variant="secondary">{look.occasion}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{look.items?.length || 0}</TableCell>
                      <TableCell className="font-mono">
                        {formatPrice(look.bundle_price_cents)}
                      </TableCell>
                      <TableCell>
                        {savingsPercent > 0 && (
                          <Badge className="bg-green-500/20 text-green-500">
                            -{savingsPercent}%
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={look.is_active ? 'default' : 'secondary'}>
                          {look.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => window.open(`/looks/${look.slug}`, '_blank')}
                            title="Voir le look"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditLook(look)}
                            title="Éditer"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(look.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                              {/* Modal édition look */}
                              <Dialog open={showEdit} onOpenChange={setShowEdit}>
                                <DialogContent>
                                  {editLook && (
                                    <form onSubmit={handleUpdateLook} className="space-y-4">
                                      <DialogHeader>
                                        <DialogTitle>Modifier le look</DialogTitle>
                                      </DialogHeader>
                                      <Input required placeholder="Nom" value={editLook.name} onChange={e => setEditLook(p => ({ ...p, name: e.target.value }))} />
                                      <Input required placeholder="Slug (unique)" value={editLook.slug} onChange={e => setEditLook(p => ({ ...p, slug: e.target.value }))} />
                                      <Input required placeholder="Image (URL)" value={editLook.image} onChange={e => setEditLook(p => ({ ...p, image: e.target.value }))} />
                                      <Input placeholder="Style" value={editLook.style} onChange={e => setEditLook(p => ({ ...p, style: e.target.value }))} />
                                      <Input placeholder="Occasion" value={editLook.occasion} onChange={e => setEditLook(p => ({ ...p, occasion: e.target.value }))} />
                                      <Input type="number" placeholder="Prix bundle (centimes)" value={editLook.bundle_price_cents} onChange={e => setEditLook(p => ({ ...p, bundle_price_cents: e.target.value }))} />
                                      <div className="flex gap-2 items-center">
                                        <label className="text-sm">Actif</label>
                                        <input type="checkbox" checked={editLook.is_active} onChange={e => setEditLook(p => ({ ...p, is_active: e.target.checked }))} />
                                      </div>
                                      <DialogFooter>
                                        <DialogClose asChild>
                                          <Button type="button" variant="secondary">Annuler</Button>
                                        </DialogClose>
                                        <Button type="submit">Enregistrer</Button>
                                      </DialogFooter>
                                    </form>
                                  )}
                                </DialogContent>
                              </Dialog>
                        </div>
                      </TableCell>
                    </motion.tr>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} sur {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}
